/**
 * logger — tiny, dependency-free structured logger.
 *
 * Goals:
 *   1. Replace ad-hoc console.* calls with a single pipeline.
 *   2. Gate `debug` / `info` to development by default; keep `warn` / `error`
 *      visible in production for actionable issues.
 *   3. Allow future hook-up to a remote service (Sentry / Datadog / LogRocket)
 *      via `setReporter()` — no call sites need to change when you add it.
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *
 *   logger.debug('HomeCMS', 'cache write skipped', err);
 *   logger.info('EpisodeDetail', 'redirected from old slug', { from, to });
 *   logger.warn('Upload', 'file > 10MB, will be compressed');
 *   logger.error('API', 'fetch failed', err);
 *
 * The first arg is a `tag` (component / module name) — keeps logs greppable.
 *
 * To wire up remote reporting later (e.g. in App.js):
 *   import * as Sentry from '@sentry/react';
 *   import { setReporter } from './utils/logger';
 *
 *   setReporter({
 *     warn:  (tag, args) => Sentry.captureMessage(`[${tag}] ${args[0]}`, 'warning'),
 *     error: (tag, args) => Sentry.captureException(args[args.length - 1] || args[0]),
 *   });
 */

const isDev = process.env.NODE_ENV === 'development';

// Default reporter: writes to the browser console.
let reporter = {
  debug: (tag, args) => {
    // eslint-disable-next-line no-console
    if (isDev) console.debug(`[${tag}]`, ...args);
  },
  info: (tag, args) => {
    // eslint-disable-next-line no-console
    if (isDev) console.info(`[${tag}]`, ...args);
  },
  warn: (tag, args) => {
    // eslint-disable-next-line no-console
    console.warn(`[${tag}]`, ...args);
  },
  error: (tag, args) => {
    // eslint-disable-next-line no-console
    console.error(`[${tag}]`, ...args);
  },
};

/**
 * Replace any subset of the reporter methods. Unspecified methods retain
 * their previous (or default) behavior. Idempotent — safe to call multiple
 * times during app bootstrap.
 *
 * @param {{debug?: Function, info?: Function, warn?: Function, error?: Function}} next
 */
export function setReporter(next) {
  reporter = { ...reporter, ...next };
}

export const logger = {
  debug: (tag, ...args) => reporter.debug(tag, args),
  info: (tag, ...args) => reporter.info(tag, args),
  warn: (tag, ...args) => reporter.warn(tag, args),
  error: (tag, ...args) => reporter.error(tag, args),
};

export default logger;
